import { Link } from "react-router";
import { NewsletterSignup } from "@/components/ui/NewsletterSignup";
import { InstagramLogo, XLogo, LinkedinLogo, GithubLogo } from "@phosphor-icons/react";
import { PLATFORM_ITEMS, SCOUT_ITEM, SOLUTION_ITEMS, RESOURCE_ITEMS } from "@/data/siteNav";
import { Chip } from "@/components/ui/ProductFrame";
import { BOOKING_URL, GITHUB_URL, signupUrl } from "@/lib/links";

const COMPANY_LINKS = [
  { href: "/company", label: "About" },
  { href: "/contact", label: "Contact" },
  { href: BOOKING_URL, label: "Book a demo" },
  { href: "/been-breached", label: "Been breached?" },
  { href: signupUrl("footer"), label: "Sign up", external: true },
];

const LEGAL_LINKS = [
  { href: "/trust-center", label: "Trust Center" },
  { href: "/legal/privacy", label: "Privacy" },
  { href: "/legal/terms", label: "Terms" },
  { href: "/legal/cookies", label: "Cookies" },
  { href: "/legal/security-disclosure", label: "Security disclosure" },
  { href: "/legal/subprocessors", label: "Sub-processors" },
  { href: "/legal/notices", label: "Notices" },
];

const SOCIAL_MEDIA_LINKS = [
  { href: "https://www.linkedin.com/company/huntbase-io", label: "LinkedIn", icon: LinkedinLogo },
  { href: "https://x.com/huntbase_io", label: "X", icon: XLogo },
  { href: "https://www.instagram.com/huntbase_io/", label: "Instagram", icon: InstagramLogo },
  { href: GITHUB_URL, label: "GitHub", icon: GithubLogo },
];

function FooterLink({
  href,
  label,
  external,
}: {
  href: string;
  label: string;
  external?: boolean;
}) {
  const cls = "text-sm text-foreground/90 transition-colors hover:text-primary-600";
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {label}
    </a>
  ) : (
    <Link to={href} className={cls}>
      {label}
    </Link>
  );
}

function Column({
  title,
  links,
  className,
}: {
  title: string;
  links: Array<{ href: string; label: string; external?: boolean }>;
  /** Platform and Solutions are hidden on phones: the menu already carries them. */
  className?: string;
}) {
  return (
    <div className={className}>
      <h3 className="mb-3 font-mono sm:mb-4 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {title}
      </h3>
      <ul className="space-y-1.5 sm:space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <FooterLink {...l} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer
      data-cta-placement="footer"
      className="border-t border-rule bg-mono-100/50"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-8 sm:gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <Link
              to="/"
              aria-label="Go to homepage"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <img
                src="/assets/logos/huntbase-logo-dark.svg"
                alt="Huntbase"
                width={120}
                height={32}
                className="h-7 w-auto"
              />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              The open threat-hunting platform. Know if you&apos;ve been hit, the same day, with
              evidence.
            </p>
            <div className="mt-5 hidden flex-wrap gap-1.5 sm:flex">
              {["OCSF", "STIX", "CACAO", "Sigma", "KQL"].map((s) => (
                <Chip key={s}>{s}</Chip>
              ))}
            </div>
            <div className="mt-6 flex gap-4" role="group" aria-label="Social media links">
              {SOCIAL_MEDIA_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={link.label === "GitHub" ? "Huntbase on GitHub" : `Follow us on ${link.label}`}
                >
                  <link.icon size={18} weight="fill" />
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-8">
            <Column
              title="Platform"
              className="hidden sm:block"
              links={[{ href: "/platform", label: "Overview" }, ...PLATFORM_ITEMS, SCOUT_ITEM]}
            />
            <Column title="Solutions" className="hidden sm:block" links={SOLUTION_ITEMS} />
            <Column
              title="Resources"
              links={[
                ...RESOURCE_ITEMS,
                { href: "/compare", label: "Compare" },
                { href: "/integrations", label: "Integrations" },
              ]}
            />
            <Column title="Company" links={COMPANY_LINKS} />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 border-t border-rule pt-8 sm:mt-12 sm:gap-6 sm:pt-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Weekly update
            </h3>
            <p className="mt-3 hidden max-w-sm text-sm leading-relaxed text-muted-foreground sm:block">
              What we're building, what we're finding, and the detections worth your time. One email
              a week.
            </p>
          </div>
          <div className="lg:col-span-7">
            <NewsletterSignup />
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-rule pt-6 sm:mt-12 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Huntbase · All rights reserved
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="mailto:hello@huntbase.io"
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              hello@huntbase.io
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
